"use client";

import { useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconExternalLink from "@/app/(components)/generics/Icons/IconExternalLink";
import IconLinkedIn from "@/app/(components)/generics/Icons/IconLinkedIn";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import Pagination from "@/app/(components)/generics/Pagination/Pagination";
import { hasPages } from "@/app/(components)/generics/Pagination/Pagination.logic";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { useFetchProspectFeedMembers } from "@/app/hooks/fetchs/prospect-search/useFetchProspectFeedMembers";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PaginationData } from "@/app/types/common/PaginationData";
import { LinkedinFeedPeriod } from "@/app/types/prospect/LinkedinFeedPeriod";
import { ProspectActionFilter } from "@/app/types/prospect/ProspectActionFilter";
import { ProspectListTab } from "@/app/types/prospect/ProspectListTab";
import { ULinkedinFeed } from "@/app/utils/ULinkedinFeed";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { feedNoticeLabel, feedOpenLabel, feedPeriodOptions } from "./ProspectLinkedinFeed.logic";
import styles from "./ProspectLinkedinFeed.module.css";

type Props = {
  avatarId?: number;
  tab?: ProspectListTab;
  actionFilter?: ProspectActionFilter;
  pagination: PaginationData;
  onPageChange: (page: number) => void;
};

export default function ProspectLinkedinFeed(props: Props) {
  const T = useT();
  const { avatarId, tab, actionFilter, pagination, onPageChange } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [period, setPeriod] = useState(LinkedinFeedPeriod.PAST_24H);

  const { data } = useFetchProspectFeedMembers({ avatarId, tab, actionFilter });

  const pages = data?.pages ?? [];
  const members = pages[pagination.page - 1] ?? [];
  const totalMemberCount = pages.reduce((total, page) => total + page.length, 0);
  const url = members.length > 0 ? ULinkedinFeed.buildUrl(members, period) : "";
  const notice = data
    ? feedNoticeLabel(T, totalMemberCount, data.prospectCount, Limits.MAX_LINKEDIN_FEED_MEMBERS)
    : null;

  const label = T(TKey.PROSPECT_FEED_TRIGGER) as string;

  if (data && totalMemberCount === 0) {
    return (
      <button type="button" className={styles.trigger} aria-label={label} disabled={true}>
        <IconLinkedIn />
      </button>
    );
  }

  return (
    <Popover
      isVisible={isOpen}
      setIsVisible={setIsOpen}
      openAfterClick={true}
      closeWhenClickOutside={true}
      isPortaled={true}
    >
      <PopoverTrigger>
        <button
          type="button"
          className={UStyles.classNames(styles.trigger, isOpen && styles.triggerActive)}
          aria-label={label}
        >
          <IconLinkedIn />
        </button>
      </PopoverTrigger>

      <PopoverContent position={{ default: { top: "8px", right: "0" } }}>
        <div className={styles.panel}>
          <span className={styles.title}>{T(TKey.PROSPECT_FEED_TITLE)}</span>

          {!data ? (
            <div className={styles.loader}>
              <PointsLoader
                centered
                color="var(--color-green-3)"
                delay={Limits.PROSPECT_SEARCH_LOADER_DELAY}
              />
            </div>
          ) : (
            <>
              <p className={styles.text}>{T(TKey.PROSPECT_FEED_TEXT)}</p>

              <div className={styles.periodList}>
                {feedPeriodOptions(T).map((option) => (
                  <button
                    key={option.period}
                    type="button"
                    className={UStyles.classNames(
                      styles.period,
                      option.period === period && styles.periodActive,
                    )}
                    onClick={() => setPeriod(option.period)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <Button
                isLink={true}
                href={url}
                external={true}
                small={true}
                full={true}
                type={ButtonType.PRIMARY}
                text={feedOpenLabel(T, members.length)}
                disabled={members.length === 0}
                afterIcon={<IconExternalLink size={14} />}
              />

              {notice && <p className={styles.note}>{notice}</p>}

              {hasPages(pagination) && (
                <div className={styles.pager}>
                  <Pagination data={pagination} onPageChange={onPageChange} maxVisiblePages={3} />
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
